//src/app/features/orders/index.tsx
/**
 * Combines:
 *   • normal Orders
 *   • Tax‑Return Projects
 *   • Tax Extensions
 *   • Subscribed Services
 */
import { DashboardContainerHeader } from 'app/layout/dashboardLayout/BoxLayout';
import {
  useLazyGetOrdersQuery,
  useGetSubscribedServicesItemsQuery,
} from 'app/services/payments/orders';
import { useGetProjectsQuery } from 'app/services/projects';
import {
  useGetTaxExtensionsQuery,
  ITaxExtension,
} from 'app/services/taxExtensions';

import {
  IOrder,
  IProject,
  ISubscribedServicesItems,
  ProjectStatusType,
} from 'app/services/responseTypes';

import PaginatedList from 'Components/Pagination/PaginatedList';
import usePagination from 'Components/Pagination/usePagination';

import OrderItem from './OrderItem';
import ProjectOrderItem from './ProjectOrderItem';
import TaxExtensionOrderItem from './TaxExtensionOrderItem';
import ServiceOrderItem from './ServiceOrderItem';

/* ────────────────────────────────────────────────────────────────────
   1. HELPERS
   ──────────────────────────────────────────────────────────────────── */

/**
 * Deduplicate **tax‑return orders**.
 * • Identify tax‑return orders by a line‑item whose `product_meta.type === "project"`.
 * • For each project title keep **one** order only – the newest priced order.
 * • An order is considered priced if `order.total_price` **or**
 *   the project line‑item’s `price_meta.amount_cents` is > 0.
 */
/**
 * Deduplicate **tax‑return orders**.
 * – If more than one order has the same project title…
 *      1)  keep the order whose state is **completed** (paid)
 *      2)  if both / neither are paid, keep the order with the
 *          most‑recent `created_at` timestamp.
 * – Non–tax‑return orders are left untouched.
 */
function dedupeTaxReturnOrders(orders: IOrder[]): IOrder[] {
  const bestByTitle = new Map<string, IOrder>(); // title → best order
  const nonTaxReturn: IOrder[] = [];

  orders.forEach((order) => {
    const projectItem = order.order_items.find(
      (oi) => oi.product_meta.type === 'project',
    );

    // Not a tax‑return order ➜ keep as‑is
    if (!projectItem) {
      nonTaxReturn.push(order);
      return;
    }

    // Ignore zero‑price drafts
    const priced =
      order.total_price > 0 || (projectItem.price_meta?.amount_cents ?? 0) > 0;
    if (!priced) return;

    const key = projectItem.product_meta.title;
    const existing = bestByTitle.get(key);

    if (!existing) {
      bestByTitle.set(key, order);
      return;
    }

    /* ---------- decide if the current order is “better” ---------- */
    const currPaid = order.state === 'completed';
    const existingPaid = existing.state === 'completed';

    const shouldReplace =
      // 1 . favour a paid order over an unpaid one
      (currPaid && !existingPaid) ||
      // 2 . if both have the same paid‑status, keep the newer one
      (currPaid === existingPaid &&
        new Date(order.created_at).getTime() >
          new Date(existing.created_at).getTime());

    if (shouldReplace) bestByTitle.set(key, order);
  });

  return [...nonTaxReturn, ...Array.from(bestByTitle.values())];
}

/**
 * Remove projects that already have a matching order.
 * • Match by identical `project_id` if backend provides it, otherwise by title.
 */
/**
 * Remove projects that already have a matching order.
 * • Match by the project’s **id** (taken from order_item.product_meta.id)
 *   and fall back to the project title.
 */
function filterProjectsWithExistingOrder(
  projects: IProject[],
  orders: IOrder[],
): IProject[] {
  const idsInOrders = new Set<number>();
  const titlesInOrders = new Set<string>();

  orders.forEach((order) => {
    order.order_items.forEach((oi) => {
      if (oi.product_meta.type === 'project') {
        // <── no more project_id ──►
        idsInOrders.add(oi.product_meta.id); // product id == project id
        titlesInOrders.add(oi.product_meta.title);
      }
    });
  });

  return projects.filter((p) => {
    const title = p.title || `${p.year} Tax Return`;
    return !idsInOrders.has(p.id) && !titlesInOrders.has(title);
  });
}

/* ────────────────────────────────────────────────────────────────────
   2. COMPONENT
   ──────────────────────────────────────────────────────────────────── */

export default function Orders() {
  /* ---------- 1. ORDERS (paginated) ---------------------------------- */
  const { data: ordersPage, Pagination } = usePagination(
    useLazyGetOrdersQuery,
    { per_page: 3, filters: {} },
  );
  const rawOrders: IOrder[] = Array.isArray(ordersPage) ? ordersPage : [];

  // Drop the empty $119/$169 pseudo‑orders created by tax‑extension checkout
  const initialOrdersArray = rawOrders.filter(
    (o) =>
      !(
        (!o.order_items || o.order_items.length === 0) &&
        (o.total_price === 16900 || o.total_price === 11900)
      ),
  );

  // Deduplicate tax‑return orders
  const ordersArray = dedupeTaxReturnOrders(initialOrdersArray);

  /* ---------- 2. TAX‑RETURN PROJECTS --------------------------------- */
  const { data: projectsResp } = useGetProjectsQuery(
    {
      per_page: 100,
      filters: { status: ['pending_payment', 'paid'] as ProjectStatusType[] },
    },
    { skip: false },
  );
  const rawProjects: IProject[] = projectsResp?.data ?? [];
  const projectsArray = filterProjectsWithExistingOrder(
    rawProjects,
    ordersArray,
  );

  /* ---------- 3. TAX EXTENSIONS -------------------------------------- */
  const { data: taxExtResp } = useGetTaxExtensionsQuery({
    per_page: 100,
    filters: {},
  });
  const taxExtensionsArray: ITaxExtension[] = (taxExtResp?.data ?? []).filter(
    (ext) =>
      (ext.payment_status === 'paid' || ext.payment_status === 'unpaid') &&
      ext.status !== 'cancelled',
  );

  /* ---------- 4. SUBSCRIBED SERVICES --------------------------------- */
  const { data: servicesResp } = useGetSubscribedServicesItemsQuery({
    per_page: 100,
    filters: { product_type: 'service' },
  });

  // Keep only paid or pending_payment services and remove those already tied to an order
  let servicesArray: ISubscribedServicesItems[] = (
    servicesResp?.data ?? []
  ).filter((s) => s.state === 'paid' || s.state === 'pending_payment');

  const orderItemIds = new Set(
    ordersArray.flatMap((o) => o.order_items.map((oi) => oi.id)),
  );
  servicesArray = servicesArray.filter((s) => !orderItemIds.has(s.id));

  /* ---------- MERGE & SORT ------------------------------------------- */
  type Combined =
    | (IOrder & { created_at: string })
    | (IProject & { created_at: string })
    | (ITaxExtension & { created_at: string })
    | (ISubscribedServicesItems & { created_at: string });

  const combined: Combined[] = [
    ...ordersArray,
    ...projectsArray,
    ...taxExtensionsArray,
    ...servicesArray,
  ].sort(
    (a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );

  /* ---------- RENDER -------------------------------------------------- */
  return (
    <>
      <DashboardContainerHeader title="Orders" />

      <PaginatedList
        data={combined}
        Pagination={Pagination}
        className="p-3"
        renderItem={(item: Combined) => {
          if ((item as IOrder).order_items) {
            return <OrderItem order={item as IOrder} />;
          } else if ((item as IProject).price) {
            return <ProjectOrderItem project={item as IProject} />;
          } else if ((item as ITaxExtension).payment_status) {
            return <TaxExtensionOrderItem extension={item as ITaxExtension} />;
          } else {
            return (
              <ServiceOrderItem service={item as ISubscribedServicesItems} />
            );
          }
        }}
      />
    </>
  );
}
